import {useEffect, useState} from 'react';
import {Box, CircularProgress, Paper, TableContainer} from '@mui/material';
import {TaxHeader, FixedAssetsTable, MotorVehicleTable, MotorVehicleRow} from '@/pages/components/tax-computation/sub';
import type {FixedAssetsRow} from '@/pages/components/tax-computation/sub/FixedAssetsTable';
import {useCompanyAnnualStore, useTaxStore} from '@/store/audit';
import type {TaxAdditionsNonCurrentAssetsVO} from '@/services/auditService/interface/tax-com';

interface TaxSubP2Props {
    annualId: number;
    p12Data?: TaxAdditionsNonCurrentAssetsVO | null;
}

function mapP12ToView(p12Data: TaxAdditionsNonCurrentAssetsVO | null | undefined) {
    const fixedAssetsRows: FixedAssetsRow[] = (p12Data?.taxNonCurrentAssets || []).map((row) => ({
        item: row?.rowName || '',
        cost: Number(row?.cost) || 0,
        tenPool: Number(row?.tenPool) || 0,
        twentyPool: Number(row?.twentyPool) || 0,
        thirtyPool: Number(row?.thirtyPool) || 0,
        hardAndSoftware: Number(row?.hardSoftware) || 0,
    }));
    const motorVehicleData: MotorVehicleRow[] = (p12Data?.taxMotorVehicles || []).map((row) => ({
        date: row?.date || '',
        item: row?.vehicleName || '',
        atCost: Number(row?.cost) || 0,
        accumulated: Number(row?.accumulated) || 0,
        sales: Number(row?.salesProceed) || 0,
        disposal: (Number(row?.gainOnDisposal) || 0) - (Number(row?.lossOnDisposal) || 0),
    }));
    return {fixedAssetsRows, motorVehicleData};
}

export function TaxSubP2({annualId, p12Data: p12DataProp}: TaxSubP2Props) {
    const getTaxComputationInfo = useCompanyAnnualStore((state) => state.getTaxComputationInfo);
    const getCompanyDetailsInfo = useCompanyAnnualStore((state) => state.getCompanyDetailsInfo);

    const initialize = useTaxStore((state) => state.initialize);
    const storeP12Data = useTaxStore((state) => state.p12Data);
    const p12Data = p12DataProp !== undefined ? p12DataProp : storeP12Data;

    const [loading, setLoading] = useState(true);
    const [taxInfo, setTaxInfo] = useState<{
        companyName: string;
        yearOfAssessment: string | null;
        presentationCurrency: string | null;
        taxFileNo: string | null;
    } | null>(null);
    const [fixedAssetsRows, setFixedAssetsRows] = useState<FixedAssetsRow[]>([]);
    const [motorVehicleData, setMotorVehicleData] = useState<MotorVehicleRow[]>([]);

    useEffect(() => {
        const loadHeader = async () => {
            setLoading(true);
            try {
                const info = await getTaxComputationInfo(annualId);
                setTaxInfo(info);

                const companyDetails = await getCompanyDetailsInfo(annualId);
                const companyId = companyDetails?.companyId;

                if (companyId) {
                    await initialize(companyId, annualId);
                } else {
                    console.error('Company ID not found');
                }
            } catch (error) {
                console.error('Failed to load tax computation data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadHeader();
    }, [annualId, getTaxComputationInfo, getCompanyDetailsInfo, initialize]);

    useEffect(() => {
        const mapped = mapP12ToView(p12Data);
        setFixedAssetsRows(mapped.fixedAssetsRows);
        setMotorVehicleData(mapped.motorVehicleData);
    }, [p12Data]);

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (!taxInfo) {
        return <Box sx={{p: 2}}>Failed to load data</Box>;
    }

    return (
        <Box sx={{p: 3}}>
            <TaxHeader
                companyName={taxInfo.companyName}
                fileNo={taxInfo.taxFileNo || ''}
                yearOfAssessment={taxInfo.yearOfAssessment}
            />

            <TableContainer component={Paper}>
                <FixedAssetsTable
                    parentCurrency={taxInfo.presentationCurrency}
                    storeRow={fixedAssetsRows}
                />

                <MotorVehicleTable
                    tableTitle="Gain On Disposal Of Motor Vehicle (Not Hire Purchase)"
                    parentCurrency={taxInfo.presentationCurrency}
                    storeRow={motorVehicleData}
                />
            </TableContainer>
        </Box>
    );
}
